/**
 * Shared roster semantics for competitive session courts (doubles + singles).
 * Court transport shape: { courtNumber, team1: id[], team2: id[], ... }
 */

import { getPlayersPerSide } from './competitiveMatchFormat';

export {
  getPlayersPerSide,
  getPlayersPerMatch,
  isSinglesFormat,
  MATCH_FORMAT_DOUBLES,
  MATCH_FORMAT_SINGLES,
} from './competitiveMatchFormat';

/** @param {{ team1?: string[], team2?: string[] }|null|undefined} court */
export function countPlayersOnCourt(court) {
  if (!court) return 0;
  const t1 = Array.isArray(court.team1) ? court.team1.length : 0;
  const t2 = Array.isArray(court.team2) ? court.team2.length : 0;
  return t1 + t2;
}

/**
 * @param {{ team1?: string[], team2?: string[] }|null|undefined} court
 * @param {'doubles'|'singles'} format
 */
export function isValidCourtRoster(court, format) {
  if (!court) return false;
  const perSide = getPlayersPerSide(format);
  const t1 = Array.isArray(court.team1) ? court.team1.filter(Boolean) : [];
  const t2 = Array.isArray(court.team2) ? court.team2.filter(Boolean) : [];
  if (t1.length > perSide || t2.length > perSide) return false;
  const seen = new Set();
  for (const id of [...t1, ...t2]) {
    if (seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

/**
 * Both teams are full for the session format (e.g. 2+2 doubles, 1+1 singles).
 * Used to decide whether an upcoming queue row still needs matchmaking.
 *
 * @param {{ team1?: string[], team2?: string[] }|null|undefined} court
 * @param {number} playersPerSide from getPlayersPerSide(matchFormat)
 */
export function isFullCourtRosterForSides(court, playersPerSide) {
  if (!court || typeof playersPerSide !== 'number' || playersPerSide < 1) return false;
  const t1 = Array.isArray(court.team1) ? court.team1.filter(Boolean) : [];
  const t2 = Array.isArray(court.team2) ? court.team2.filter(Boolean) : [];
  return t1.length === playersPerSide && t2.length === playersPerSide;
}

/** @param {unknown} entry */
function playerIdFromHistoryEntry(entry) {
  if (typeof entry === 'string') return String(entry);
  if (entry && typeof entry === 'object' && entry.id) return String(entry.id);
  return null;
}

/**
 * Parse team rosters from a match_history entry with normalised string player IDs.
 * @param {object|null|undefined} game
 * @returns {{ team1: string[], team2: string[] }}
 */
export function parseGameTeamsFromHistoryEntry(game) {
  if (!game) return { team1: [], team2: [] };
  let team1 = game.team1;
  let team2 = game.team2;
  if ((!Array.isArray(team1) || !Array.isArray(team2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
    [team1, team2] = game.teams;
  }
  const normalise = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map(playerIdFromHistoryEntry)
      .filter(Boolean);
  return {
    team1: normalise(team1),
    team2: normalise(team2),
  };
}

/**
 * Games a player participated in, in session timeline order (oldest → newest).
 * Order is derived from each entry's position in matchHistory, so it stays correct
 * whether SQL materialised ASC or DESC.
 *
 * @param {Array} matchHistory
 * @param {string} playerId
 * @returns {Array<{ team1: string[], team2: string[], allPlayerIds: Set<string>, sessionIndex: number }>}
 */
export function getPersonalGamesForPlayer(matchHistory, playerId) {
  const focusId = String(playerId);
  if (!Array.isArray(matchHistory) || matchHistory.length === 0) {
    return [];
  }

  const games = [];
  matchHistory.forEach((game, sessionIndex) => {
    const { team1, team2 } = parseGameTeamsFromHistoryEntry(game);
    const all = [...team1, ...team2];
    if (!all.includes(focusId)) {
      return;
    }
    games.push({
      team1,
      team2,
      allPlayerIds: new Set(all),
      sessionIndex,
    });
  });

  games.sort((a, b) => a.sessionIndex - b.sessionIndex);
  return games;
}

/**
 * Count consecutive personal games (newest-first) where both players shared a court.
 * Teammate or opponent both count. Partnership pairs should be excluded by callers.
 *
 * @returns {number}
 */
export function countPersonalConsecutiveCourtStreak(player1Id, player2Id, matchHistory) {
  const id1 = String(player1Id);
  const id2 = String(player2Id);

  const streakForPlayer = (focusId, otherId) => {
    const personalGames = getPersonalGamesForPlayer(matchHistory, focusId);
    let streak = 0;
    for (let i = personalGames.length - 1; i >= 0; i -= 1) {
      if (personalGames[i].allPlayerIds.has(otherId)) {
        streak += 1;
      } else {
        break;
      }
    }
    return streak;
  };

  return Math.max(streakForPlayer(id1, id2), streakForPlayer(id2, id1));
}

/** @param {string} id1 @param {string} id2 */
export function getPairInteractionKey(id1, id2) {
  return [id1, id2].sort().join('-');
}

/**
 * Increment interaction counts for one finished game (all pairs on court).
 * Mutates and returns the same map.
 * @param {Record<string, number>} map
 * @param {string[]} team1
 * @param {string[]} team2
 */
export function incrementPairInteractionsForFinishGame(map, team1, team2) {
  const t1ids = (Array.isArray(team1) ? team1 : []).filter(Boolean);
  const t2ids = (Array.isArray(team2) ? team2 : []).filter(Boolean);
  const all = [...t1ids, ...t2ids];
  if (all.length < 2) return map;

  const t1set = new Set(t1ids);
  const t2set = new Set(t2ids);

  for (let i = 0; i < all.length; i++) {
    for (let j = i + 1; j < all.length; j++) {
      const key = getPairInteractionKey(all[i], all[j]);
      map[key] = (map[key] || 0) + 1;
      const sameTeam =
        (t1set.has(all[i]) && t1set.has(all[j])) || (t2set.has(all[i]) && t2set.has(all[j]));
      if (sameTeam) {
        const sameTeamKey = `${key}_sameTeam`;
        map[sameTeamKey] = (map[sameTeamKey] || 0) + 1;
      }
    }
  }
  return map;
}

/** @param {string[]} a @param {string[]} b */
function sameUuidArraysUnordered(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((id, i) => id === sb[i]);
}

/**
 * Overlay the court game being scored onto match history + interaction counts.
 * Matchmaking runs before commit_score_transition, so without this overlay the
 * engine is one game behind on variety signals.
 *
 * Idempotent when SQL materialisation already appended the pending row.
 *
 * @param {{
 *   matchHistory?: Array,
 *   playerInteractions?: Record<string, number>,
 *   totalGamesCompleted?: number,
 * }} snapshot
 * @param {{ team1?: string[], team2?: string[] }|null|undefined} pendingFinishGame
 */
export function applyPendingFinishGameOverlay(snapshot, pendingFinishGame) {
  if (!snapshot || !pendingFinishGame) return snapshot;

  const team1 = (pendingFinishGame.team1 || []).filter(Boolean);
  const team2 = (pendingFinishGame.team2 || []).filter(Boolean);
  if (team1.length + team2.length < 2) return snapshot;

  const history = Array.isArray(snapshot.matchHistory) ? [...snapshot.matchHistory] : [];
  const last = history[history.length - 1];
  const alreadyOverlaid =
    last?.gameId === 'pending-finish' &&
    sameUuidArraysUnordered(last.team1 || [], team1) &&
    sameUuidArraysUnordered(last.team2 || [], team2);

  if (!alreadyOverlaid) {
    history.push({
      gameId: 'pending-finish',
      team1,
      team2,
    });
  }

  const interactions = { ...(snapshot.playerInteractions || {}) };
  if (!alreadyOverlaid) {
    incrementPairInteractionsForFinishGame(interactions, team1, team2);
  }

  return {
    ...snapshot,
    matchHistory: history,
    playerInteractions: interactions,
    totalGamesCompleted: Math.max(
      snapshot.totalGamesCompleted ?? 0,
      history.length,
    ),
  };
}

/** @param {Array<{ team1?: unknown[], team2?: unknown[], teams?: unknown[][] }>} matchHistory */
export function derivePlayerInteractionsFromMatchHistory(matchHistory) {
  const map = {};
  if (!Array.isArray(matchHistory)) return map;

  for (const game of matchHistory) {
    if (!game) continue;
    let team1;
    let team2;
    if (Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    } else {
      team1 = game.team1;
      team2 = game.team2;
    }
    const t1ids = (Array.isArray(team1) ? team1 : []).map(playerIdFromHistoryEntry).filter(Boolean);
    const t2ids = (Array.isArray(team2) ? team2 : []).map(playerIdFromHistoryEntry).filter(Boolean);
    const all = [...t1ids, ...t2ids];
    if (all.length < 2) continue;
    const t1set = new Set(t1ids);
    const t2set = new Set(t2ids);

    for (let i = 0; i < all.length; i++) {
      for (let j = i + 1; j < all.length; j++) {
        const p1 = all[i];
        const p2 = all[j];
        const key = getPairInteractionKey(p1, p2);
        map[key] = (map[key] || 0) + 1;
        const sameTeam = (t1set.has(p1) && t1set.has(p2)) || (t2set.has(p1) && t2set.has(p2));
        if (sameTeam) {
          const sameTeamKey = `${key}_sameTeam`;
          map[sameTeamKey] = (map[sameTeamKey] || 0) + 1;
        }
      }
    }
  }
  return map;
}

/**
 * Prefer persisted session_player_interactions; derive from matchHistory when DB counts are absent.
 * @param {Record<string, number>|null|undefined} storedInteractions
 * @param {Array} matchHistory
 */
export function coalescePlayerInteractionsForMatchmaking(storedInteractions, matchHistory) {
  const stored =
    storedInteractions && typeof storedInteractions === 'object' && !Array.isArray(storedInteractions)
      ? storedInteractions
      : {};
  const hasStoredTotals = Object.keys(stored).some(
    (key) => !key.endsWith('_sameTeam') && Number(stored[key]) > 0,
  );
  if (hasStoredTotals) return stored;
  return derivePlayerInteractionsFromMatchHistory(matchHistory);
}

/**
 * Waitlist snapshot can briefly include players already placed on active or upcoming courts
 * (React state ordering / debounced save). Normalized DB expects one placement per player.
 * @returns {{ waitingList: string[], removedBecausePlacedElsewhere: string[] }}
 */
export function deriveConsistentWaitingList(waitingList, courtAssignments, nextAssignments) {
  const placed = new Set([
    ...collectPlayerIdsFromAssignments(courtAssignments),
    ...collectPlayerIdsFromAssignments(nextAssignments),
  ]);
  const removedBecausePlacedElsewhere = [];
  const waitingListOut = [];
  for (const id of waitingList || []) {
    if (!id) continue;
    if (placed.has(id)) removedBecausePlacedElsewhere.push(id);
    else waitingListOut.push(id);
  }
  return { waitingList: waitingListOut, removedBecausePlacedElsewhere };
}

/** @param {Array<{ team1?: string[], team2?: string[] }>} assignments */
export function collectPlayerIdsFromAssignments(assignments) {
  const ids = [];
  for (const row of assignments || []) {
    const t1 = Array.isArray(row?.team1) ? row.team1 : [];
    const t2 = Array.isArray(row?.team2) ? row.team2 : [];
    for (const id of [...t1, ...t2]) {
      if (id) ids.push(id);
    }
  }
  return ids;
}

/**
 * Find duplicate player ids across waiting list, active courts, and upcoming matches.
 * @returns {{ duplicateIds: string[], contexts: Map<string, string[]> }}
 */
export function findCrossContextPlayerDuplicates(waitingList, courtAssignments, nextAssignments) {
  const contexts = new Map();
  const add = (playerId, label) => {
    if (!playerId) return;
    const arr = contexts.get(playerId) || [];
    arr.push(label);
    contexts.set(playerId, arr);
  };

  (waitingList || []).forEach((id, i) => add(id, `waitingList[${i}]`));
  (courtAssignments || []).forEach((court, ci) => {
    const t1 = court?.team1 || [];
    const t2 = court?.team2 || [];
    t1.forEach((id, ti) => add(id, `court[${ci}].team1[${ti}]`));
    t2.forEach((id, ti) => add(id, `court[${ci}].team2[${ti}]`));
  });
  (nextAssignments || []).forEach((match, mi) => {
    const t1 = match?.team1 || [];
    const t2 = match?.team2 || [];
    t1.forEach((id, ti) => add(id, `next[${mi}].team1[${ti}]`));
    t2.forEach((id, ti) => add(id, `next[${mi}].team2[${ti}]`));
  });

  const duplicateIds = [];
  contexts.forEach((labels, id) => {
    if (labels.length > 1) duplicateIds.push(id);
  });
  return { duplicateIds, contexts };
}
